/** One OCR word with its box in image pixels and confidence 0..1. */
export interface OcrCell {
  text: string;
  x: number;
  y: number;
  w: number;
  h: number;
  conf: number;
}

/** Cells that share a baseline, left to right. */
export interface OcrRow {
  cy: number;
  cells: OcrCell[];
}

/** A column model for one nine: x = a + b * holeNumber. */
export interface HeaderFit {
  from: 1 | 10;
  hasLabel: boolean;
  cols: Array<{ n: number; x: number }>;
  spacing: number;
}

export interface HeaderHit {
  fit: HeaderFit;
  row: OcrRow;
}

export type NineHeaders = Partial<Record<1 | 10, HeaderHit>>;

export type HoleValues = Record<number, number>;

export interface ParsedPlayer {
  scoreFormat?: "stableford";
  scoringBasis?: "net" | "gross";
  points?: HoleValues;
  stablefordTotal?: number;
  visibleHoles?: number[];
  name: string | null;
  course: string | null;
  playedDate?: string | null | undefined;
  hcp: number | null;
  /** Gross strokes per hole number. */
  holes: HoleValues;
  net: HoleValues;
  /** Confidence 0..1 per hole in `holes`. */
  conf: HoleValues;
  par: HoleValues;
  si: HoleValues;
  out: number | null;
  inn: number | null;
  total: number | null;
  netTotal: number | null;
  reconstructed: number[];
  fingerprint: string;
}

export type ScorecardKind =
  | "detailed-18"
  | "front-9"
  | "back-9"
  | "leaderboard"
  | "summary"
  | "single-hole"
  | "unsupported"
  | "unreadable";

export interface ParseResult {
  kind: ScorecardKind;
  players: ParsedPlayer[];
  issues: string[];
}

export const usableKinds: ReadonlySet<ScorecardKind> = new Set([
  "detailed-18",
  "front-9",
  "back-9",
]);

export function emptyPlayer(): ParsedPlayer {
  return {
    name: null,
    course: null,
    hcp: null,
    holes: {},
    net: {},
    conf: {},
    par: {},
    si: {},
    out: null,
    inn: null,
    total: null,
    netTotal: null,
    reconstructed: [],
    fingerprint: "",
  };
}

export function holeNumbers(values: HoleValues): number[] {
  return Object.keys(values)
    .map(Number)
    .sort((a, b) => a - b);
}

export function has(values: HoleValues, hole: number): boolean {
  return Object.prototype.hasOwnProperty.call(values, hole);
}

export function sumHoles(values: HoleValues, holes: number[]): number {
  return holes.reduce((sum, hole) => sum + (values[hole] ?? 0), 0);
}
